import AuthService from './AuthService';

class DocumentService {
    constructor() {
        const rawBaseUrl = process.env.REACT_APP_CHATBOT_API_URL || process.env.REACT_APP_API_URL || '';
        this.apiBaseUrl = String(rawBaseUrl).replace(/\/+$/, '');
    }

    getAuthHeaders(extraHeaders) {
        const token = AuthService.getAccessToken();
        const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
        return {
            ...authHeader,
            ...(extraHeaders || {}),
        };
    }

    async requestPresign(kbaseId, filename, options) {
        if (!kbaseId) throw new Error('kbaseId is required');
        if (!filename) throw new Error('filename is required');

        const urlBase = `${this.apiBaseUrl}/private/kbases/${encodeURIComponent(kbaseId)}/documents`;
        const folder = String(options?.folder ?? '').trim();
        const qs = new URLSearchParams();
        if (folder) qs.set('folder', folder);
        const url = qs.toString() ? `${urlBase}?${qs.toString()}` : urlBase;
        const ocrRequired = Boolean(options?.ocrRequired);
        const payload = {
            documentType: 'file',
            filename,
            fileAction: 'presign',
            ocrRequired,
            folder: folder || undefined,
        };

        const response = await fetch(url, {
            method: 'POST',
            headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        return await response.json();
    }

    async markUploaded(kbaseId, documentId, filename, options) {
        if (!kbaseId) throw new Error('kbaseId is required');
        if (!documentId) throw new Error('documentId is required');
        if (!filename) throw new Error('filename is required');

        const urlBase = `${this.apiBaseUrl}/private/kbases/${encodeURIComponent(kbaseId)}/documents`;
        const folder = String(options?.folder ?? '').trim();
        const qs = new URLSearchParams();
        if (folder) qs.set('folder', folder);
        const url = qs.toString() ? `${urlBase}?${qs.toString()}` : urlBase;
        const ocrRequired = Boolean(options?.ocrRequired);
        const payload = {
            documentType: 'file',
            documentId,
            filename,
            fileAction: 'uploaded',
            ocrRequired,
            folder: folder || undefined,
        };

        const response = await fetch(url, {
            method: 'POST',
            headers: this.getAuthHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const text = await response.text();
        try {
            return text ? JSON.parse(text) : { ok: true };
        } catch {
            return { ok: true };
        }
    }

    async getDocumentChunks(kbaseId, documentId) {
        if (!kbaseId) throw new Error('kbaseId is required');
        if (!documentId) throw new Error('documentId is required');

        const url = `${this.apiBaseUrl}/private/kbases/${encodeURIComponent(kbaseId)}/documents/${encodeURIComponent(documentId)}/chunks`;
        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        return Array.isArray(data) ? data : [];
    }

    async getUrlPreview(targetUrl) {
        if (!targetUrl) throw new Error('targetUrl is required');
        const params = new URLSearchParams({ url: targetUrl });
        const response = await fetch(`${this.apiBaseUrl}/private/url-preview?${params.toString()}`, {
            method: 'GET',
            headers: this.getAuthHeaders(),
        });
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        return await response.blob();
    }
}

const documentService = new DocumentService();
export { DocumentService };
export default documentService;
